/**
 * syncMetaStore.ts
 *
 * Raw read/write for the `syncMeta` object store.
 * Always one record keyed "global".
 * Tracks global sync health — status, pending count, last sync time.
 */

import { appDB } from "../db/client";
import type { SyncMetaRecord, SyncStatus } from "../db/schema";

// ─── Read ─────────────────────────────────────────────────────────

/**
 * Get the current sync meta record.
 * Returns a safe default if not yet initialized.
 */
export async function getSyncMeta(): Promise<SyncMetaRecord> {
  const record = await appDB.syncMeta.get("global");
  return (
    record ?? {
      id: "global",
      syncStatus: "idle",
      pendingCount: 0,
      lastSuccessfulSyncAt: null,
      clientId: crypto.randomUUID(),
      workspaceId: null,
    }
  );
}

// ─── Writes ───────────────────────────────────────────────────────

/**
 * Update the sync status.
 * Called by the watcher at every state transition.
 */
export async function setSyncStatus(status: SyncStatus): Promise<void> {
  await appDB.syncMeta.update("global", { syncStatus: status });
}

/**
 * Update the pending count.
 * Called after every mutation and after every successful sync batch.
 */
export async function setPendingCount(count: number): Promise<void> {
  await appDB.syncMeta.update("global", { pendingCount: count });
}

/**
 * Atomically counts pending ops and updates syncMeta in a single transaction.
 * Prevents race conditions where other mutations modify the queue between
 * count and update.
 */
export async function refreshPendingCount(): Promise<void> {
  await appDB.transaction("rw", [appDB.syncQueue, appDB.syncMeta], async () => {
    const count = await appDB.syncQueue.where("isSync").equals(0).count();
    await appDB.syncMeta.update("global", { pendingCount: count });
  });
}

/**
 * Record a successful sync completion.
 * Updates lastSuccessfulSyncAt and resets status to idle.
 */
export async function recordSyncSuccess(): Promise<void> {
  await appDB.syncMeta.update("global", {
    syncStatus: "idle",
    lastSuccessfulSyncAt: Date.now(),
  });
}

/**
 * Update the active workspace ID.
 * Called when the user switches workspaces.
 */
export async function setActiveWorkspace(workspaceId: string): Promise<void> {
  await appDB.syncMeta.update("global", { workspaceId });
}

/**
 * Full update — replaces the entire syncMeta record.
 * Used during app initialization.
 */
export async function upsertSyncMeta(
  partial: Partial<Omit<SyncMetaRecord, "id">>
): Promise<void> {
  const current = await getSyncMeta();
  await appDB.syncMeta.put({
    ...current,
    ...partial,
    id: "global",
  });
}
